import type { Recipe, RecipeIngredient, MealPlan, MealPlanIngredient } from '@/types/meal';
import { formatLocalDate, parseLocalDate } from '@/lib/dateUtils';
import { isAuthRequired } from '@/lib/environment';

const RECIPES_CACHE_PREFIX = 'clando-recipes-cache-v1:';
const MEAL_PLANS_CACHE_PREFIX = 'clando-meal-plans-cache-v1:';
const LAST_USER_ID_KEY = 'team-v-does-last-user-id';

type CachePayload<T> = {
  v: 1;
  teamId: string;
  userId: string | null;
  savedAt: string;
  data: T[];
};

// ─── Serialization helpers ─────────────────────────────────────────────────

const serializeIngredient = (ing: RecipeIngredient) => ({
  ...ing,
  createdAt: ing.createdAt.toISOString(),
});

const deserializeIngredient = (raw: any): RecipeIngredient => ({
  ...raw,
  createdAt: new Date(raw.createdAt),
});

const serializePlanIngredient = (ing: MealPlanIngredient) => ({
  ...ing,
  createdAt: ing.createdAt.toISOString(),
});

const deserializePlanIngredient = (raw: any): MealPlanIngredient => ({
  ...raw,
  createdAt: new Date(raw.createdAt),
});

export const serializeRecipe = (r: Recipe) => ({
  ...r,
  createdAt: r.createdAt.toISOString(),
  updatedAt: r.updatedAt.toISOString(),
  deletedAt: r.deletedAt ? r.deletedAt.toISOString() : null,
  ingredients: r.ingredients?.map(serializeIngredient),
});

export const deserializeRecipe = (raw: any): Recipe => ({
  ...raw,
  createdAt: new Date(raw.createdAt),
  updatedAt: new Date(raw.updatedAt),
  deletedAt: raw.deletedAt ? new Date(raw.deletedAt) : null,
  ingredients: Array.isArray(raw.ingredients) ? raw.ingredients.map(deserializeIngredient) : undefined,
});

export const serializeMealPlan = (m: MealPlan) => ({
  ...m,
  plannedDate: formatLocalDate(m.plannedDate),
  createdAt: m.createdAt.toISOString(),
  updatedAt: m.updatedAt.toISOString(),
  cookedAt: m.cookedAt ? m.cookedAt.toISOString() : null,
  deletedAt: m.deletedAt ? m.deletedAt.toISOString() : null,
  ingredients: m.ingredients?.map(serializePlanIngredient),
});

export const deserializeMealPlan = (raw: any): MealPlan => ({
  ...raw,
  plannedDate: parseLocalDate(raw.plannedDate),
  createdAt: new Date(raw.createdAt),
  updatedAt: new Date(raw.updatedAt),
  cookedAt: raw.cookedAt ? new Date(raw.cookedAt) : null,
  deletedAt: raw.deletedAt ? new Date(raw.deletedAt) : null,
  ingredients: Array.isArray(raw.ingredients) ? raw.ingredients.map(deserializePlanIngredient) : undefined,
});

// ─── Generic cache helpers (mirrors stockCache) ────────────────────────────

const getCacheKey = (prefix: string, teamId: string) => `${prefix}${teamId}`;

const loadFromCache = <T>(prefix: string, teamId: string, deserializer: (raw: any) => T): T[] => {
  if (isAuthRequired()) {
    try {
      if (!localStorage.getItem(LAST_USER_ID_KEY)) return [];
    } catch { return []; }
  }
  try {
    const raw = localStorage.getItem(getCacheKey(prefix, teamId));
    if (!raw) return [];
    const parsed: CachePayload<T> = JSON.parse(raw);
    if (!parsed || parsed.v !== 1 || parsed.teamId !== teamId || !Array.isArray(parsed.data)) return [];
    const currentUserId = (() => {
      try { return localStorage.getItem(LAST_USER_ID_KEY); } catch { return null; }
    })();
    if (parsed.userId && currentUserId && parsed.userId !== currentUserId && isAuthRequired()) return [];
    return parsed.data.map(deserializer);
  } catch {
    return [];
  }
};

const saveToCache = <T>(prefix: string, teamId: string, data: T[], serializer: (item: T) => any) => {
  try {
    const userId = (() => {
      try { return localStorage.getItem(LAST_USER_ID_KEY); } catch { return null; }
    })();
    const payload: CachePayload<any> = {
      v: 1,
      teamId,
      userId,
      savedAt: new Date().toISOString(),
      data: data.map(serializer),
    };
    localStorage.setItem(getCacheKey(prefix, teamId), JSON.stringify(payload));
  } catch {
    // ignore quota / privacy-mode errors
  }
};

// ─── Exported helpers ─────────────────────────────────────────────────────

export const loadRecipesFromCache = (teamId: string) =>
  loadFromCache(RECIPES_CACHE_PREFIX, teamId, deserializeRecipe);
export const saveRecipesToCache = (teamId: string, items: Recipe[]) =>
  saveToCache(RECIPES_CACHE_PREFIX, teamId, items, serializeRecipe);

export const loadMealPlansFromCache = (teamId: string) =>
  loadFromCache(MEAL_PLANS_CACHE_PREFIX, teamId, deserializeMealPlan);
export const saveMealPlansToCache = (teamId: string, items: MealPlan[]) =>
  saveToCache(MEAL_PLANS_CACHE_PREFIX, teamId, items, serializeMealPlan);
